import { getCurrentInstance, DefineProps, ComponentInternalInstance, getCurrentScope } from "vue";

import { fixKennungForProperty } from "@/DataHelper";
import { scrollIntoView, showDialog } from "@/UIHelper";
import { formatDate } from "@/editor/CalenderHelper";
import { globalsBezeichnung } from "@/local";
import { Globals } from '@/Globals';
import AUApi from '@/api';
import { getEditPageByInstanceId } from "@/DataLoaderStatic";

export interface CommonEditControlEmits {
  (e: 'mountFinished'): void;
  (e: 'change', value: unknown): void;
  (e: 'input', value: unknown): void;
  (e: 'seteditvalue', value: unknown): void;
  (e: 'changeValue', value: unknown): void;
  (e: 'initValue', value: unknown): void;
  (e: 'onReloadData'): void;
  (e: 'update:modelValue', value: any): void;
}

export interface EditControlProps {
  description?: string;
  kennung?: string;
  query?: string;
  kennungKz?: string;
  stammtyp?: number;

  // kann column überschreiben oder wird verwendet wenn ohne column
  readonly?: boolean;
  required?: boolean;
  initValue?: any;
  queryColumn?: DataColumn;
  modelValue?: any;

  siteData?: any; // any component renderd in place with a prop for siteData
  disableValueChangeTracker?: boolean;
  autofocus?: boolean;
}
export interface SetEditValueParameters {
  value: any;
  field: DataColumn;
}

let disableValueChangeTracker!: boolean;
export function scrollMeIntoView(e: Event) {
  scrollIntoView(e);
}
// export function requiredRender() { return required; }
export function getEditPageData(instance: ComponentInternalInstance | null = getCurrentInstance(), setEditComponent: boolean = true): {
  pageParams?: DataPageParameter;
  uiData?: DataPageUIData;
} {
  if (!instance) throw new Error("no vue instance found");
  if (setEditComponent) {
    (<any>instance).imAEditComponent = true; // ugly remark - mal schauen ob ma das noch brauchen...
  }
  let name = instance.type.__name ?? instance.type.name;

  while (instance && !(<EditPageInterface><any>instance).setupState?.pageParams) {
    var cached = getEditPageByInstanceId(instance.uid);
    if (cached) return cached;

    instance = instance!.parent;
    name += ' -> ' + (instance?.type.__name ?? instance?.type.name);
  };

  if (!instance) {
    console.warn("no parent edit page vue instance found - path: " + name);
    return {};
  }

  var page = (<EditPageInterface><any>instance);

  return { pageParams: page.setupState.pageParams, uiData: page.setupState.uiData };
}

export function getDefaultEditorControlConfig<
  ControlConfig extends EditorControlConfig = EditorControlConfig,
  ControlProps extends EditControlProps = EditControlProps,
  ControlUiData extends EditorControlUiData = EditorControlUiData
>(
  props: DefineProps<ControlProps, never>,
  emitFn: CommonEditControlEmits, initialUIData?: any)
  : { config: ControlConfig, ui: ControlUiData } {
  const instance = getCurrentInstance();
  const page = getEditPageData(instance);

  const recheckPageOnMount = (!page.uiData && !page.pageParams)

  // wenn kennung oder kennkz gesetzt dann von da holen - wenn nicht dann leere konfig zurückgeben
  if (!recheckPageOnMount && (props.kennung || props.kennungKz || props.queryColumn)) {
    const column = props.queryColumn ?? (page.uiData ? getColumnByKennung(page.uiData.columns, props.kennung, props.kennungKz) : undefined) ?? undefined;

    // initvalue richtig rausrechnen...
    if (!column) {
      throw new Error("cannot find column kennung: " + props.kennung + " kennungKz:" + props.kennungKz);
    }
    const config = <ControlConfig>{
      vueInstance: instance,
      column,
      requiredMessage: requiredMessage,
      emit: emitFn,
      validate: () => true, // default immer valid
      recheckPageOnMount
    };
    const uiData = <ControlUiData>{
      title: column.title ?? props.description,
      description: column.title ?? props.description,
      data: props.initValue ?? page.uiData?.data[column.name],
      showMutable: false,
      stammtyp: props.stammtyp,
      required: column.meta.required,
    };

    if (initialUIData) Object.assign(uiData, initialUIData);
    if (page.uiData) {
      let value = getInitValueBase(props.initValue ?? (page.uiData.data && page.uiData.data[0] && page.uiData.data[0][column.name]), config, uiData);
      config.initValue = value;
      config.emit("update:modelValue", value);
      if (value) uiData.data = value;
    }

    return {
      config,
      ui: uiData,
    };

  } else {
    const config = <ControlConfig>{
      column: props.queryColumn,
      requiredMessage: requiredMessage,
      emit: emitFn,
      validate: () => true, // default immer valid
      recheckPageOnMount
    };
    const uiData = <ControlUiData>{
      title: props.description,
      description: props.description,
      data: {},
      showMutable: false,
      readonly: props.readonly,
      required: props.readonly,
      stammtyp: props.stammtyp,
    };
    if (initialUIData) Object.assign(uiData, initialUIData);

    return {
      config,
      ui: uiData,
    };
  }
}

// validation
let requiredMessage: string = globalsBezeichnung(
  Globals.Begriff,
  "Web_Pflichtfeld",
  "Pflichtfeld"
);
export function getColumnByKennung(
  columns: DataColumn[],
  kennung?: string,
  kz?: string
) {
  if (kennung)
    kennung = fixKennungForProperty(kennung);
  else if (!kennung || !columns.values) {
    return null;
  }
  kennung = kennung.toLowerCase();
  const col = columns.find(
    c =>
      c.kennung &&
      fixKennungForProperty(c.kennung).toLowerCase() === kennung &&
      (!kz || c.meta.kz === kz)
  );
  return col;
}

export async function mounted(uiData: EditorControlUiData, props: EditControlProps, config: EditorControlConfig) {
  //mountedBase();
  console.log("mounted " + config.recheckPageOnMount);
  if (config.recheckPageOnMount) {
    console.log("recheckPageOnMount");
    const newData = getDefaultEditorControlConfig(props, config.emit);
    Object.assign(config, newData.config);
    Object.assign(uiData, newData.ui);
  }
  uiData.showMutable = config.column?.meta.invisible ? false : true;
  if (config.column?.meta.readOnly || props.readonly) {
    uiData.readonly = true;
    uiData.disabled = true;
  }

}

export function loadStichtagfromConfig(config: EditorControlConfig) {
  const column = config.column;
  const aic = config.aic!;
  if (column?.value) {
    const aicEigenschaft = column.value.aicEig;
    const aicStamm = aic;
    AUApi.Current.getStichtag(aicEigenschaft, aicStamm).then((x: any) => {
      const stichtage = x.data;
      if (stichtage) {
        showDialog({
          showTitle: false,
          title: "",
          width: 800,
          site: "BaseStichtage",
          fullscreen: false,
          data: {
            zeitbereich: AUApi.Current.zeitbereich,
            data: stichtage,
          },
          onClose: () => {
            return true;
          },
        });
      }
    });
  }
}


export function getInitValueBase(initValue: any, config: EditorControlConfig, uiData: EditorControlUiData): any {
  const column = config.column;
  if (column && column.meta && !initValue) {
    if (column.meta.required) {
      initValue = getDefault();
      uiData.errorText = requiredMessage;
      valueChanged(config, uiData, initValue, true);
    }
    if (column.meta.keinVorfuellen) {
      return null;
    } // immer leer machen...


    if (column.meta.neuArt) {
      if (column.meta.neuArt === "Last") {
        // wenn nicht explizit übergben - dann gilt das initValue als lastValue
        if (!config.lastValue) {
          config.lastValue = initValue;
        }
        return config.lastValue;
      }
      if (column.meta.neuArt === "Limit") { // Max Wert bzw erste Wert der Combobox
        if (!config.lastValue) {
          config.lastValue = initValue;
        }
        return config.lastValue;
      }
      if (column.meta.neuArt === "kein") {
        return null;
      }
      if (column.meta.neuArt === "Sync") {
        // noch umbauen - da soll dann das VOn bei Datum vorgefüllt werden!
        // wenn nicht explizit übergben - dann gilt das initValue als lastValue
        if (!config.lastValue) {
          config.lastValue = initValue;
        }
        return config.lastValue;
      }
    }
  }
  return initValue;
}

export function getDefault() {
  return "";
}

export function valueChanged(config: EditorControlConfig, uiData: EditorControlUiData, data: any, ausInit?: boolean) {
  try {
    console.log("value changed:" + data);
    const dataLog = JSON.stringify(data);
    // if (column.value?.props.kennung === "STAMMSTICHTAG") {
    //   $api.stichtag = data;
    // }
    if (config.column?.meta?.stichtag && !ausInit && AUApi.Current.stichtag) {
      const aicEigenschaft = config.column.aicEig;
      //const aicStamm = config.aic;
      const stichtag = new Date(AUApi.Current.stichtag);
      const dateStichtag = stichtag.toISOStringWithTZ().substr(0, 10);
      // brauch den Änderungsstichtag aus BaseEditor...
      AUApi.Current.getStichtag(aicEigenschaft, config.aicStamm!, dateStichtag).then((x: any) => {
        const stichtage = x.data.data;
        if (stichtage && stichtage.length > 0) {
          const abDatum = stichtage[0].ab;
          const ab = formatDate(new Date(abDatum), "dd.MM.yyyy");
          const daten = stichtage[0].daten.bezeichnung;
          uiData.errorText = "nachfolgender Stichtag vorhanden: " + ab + ", " + daten;
        }
      });
    }
    config.validate();
    if (ausInit) {
      // es wurde nur Initialisiert aber nicht geändert!
      config.emit("initValue", data);
    } else {
      uiData.style = "color: red lighten-2";
      config.emit("changeValue", data);
    }
    config.emit("change", data);
    config.emit("input", data);
    config.emit("seteditvalue", { value: data, field: config.column });
    uiData.isChanged = (config.initValue ?? "") !== (data ?? "");
    // value muss in DATA übernommen werden - wenn Required.. damit vorinitialisierte Werte immer gespeichert werden!
    if (!ausInit || config?.column?.meta.required) {
      setValueOnEditPageData(config, uiData, data, ausInit);
    }
    if (data && uiData.isChanged && config.column) {
      config.emit("update:modelValue", data);
    }

    return true;
  } catch (e) {
    console.error(
      "Error in field '" + uiData.description + "' on valueChanged:" + e
    );
  }
}

// export function findParentComponent(validate: (component: ComponentInternalInstance, setupState: any) => boolean) {
//   let instance = getCurrentInstance()?.parent;
//   while (instance) {
//     if ((<any>instance).setupState) {
//       if (validate(instance, (<any>instance).setupState)) {
//         return instance;
//       }
//     }
//     instance = instance.parent;
//   }
//   return null;
// }

export function setValueOnEditPageData(config: EditorControlConfig, uiData: EditorControlUiData, data: any, ausInit?: boolean): void {
  if (!config.column) return;
  const page = getEditPageData(config.vueInstance);
  if (!page.uiData) return;
  const columnName = config.column!.name;
  if (Array.isArray(page.uiData.data)) {
    // Leeres Array → neuen Row anlegen
    if (page.uiData.data.length === 0) {
      page.uiData.data.push({} as QueryDataRow);
    }
    const datarow = page.uiData.data[0] as QueryDataRow;
    datarow[columnName] = data;
    page.uiData.data[0] = datarow;
  } else {
    // Noch kein Array (initiales {} im BaseEditor) → in Array mit Row überführen,
    // damit data[0] für die Speicherlogik korrekt zugreifbar ist.
    // Kein data[0] = data Rückschreiben → verhindert die Zirkelreferenz.
    const datarow = (page.uiData.data ?? {}) as QueryDataRow;
    datarow[columnName] = data;
    page.uiData.data = [datarow] as unknown as QueryData;
  }
}

//#region Cache
// cache für die editComponent - damit die immer wieder neu gefunden werden kann (von ValueChangeTracker)
declare interface EditComponentCache {
  [id: number | string]: EditComponentCacheEntry;
}
declare interface EditComponentCacheEntry {
  uiData: EditorControlUiData;
  instance: ComponentInternalInstance
}
declare const editComponentCache: EditComponentCache;

export function registerEditComponent(
  instance: ComponentInternalInstance,
  uiData: EditorControlUiData
) {
  if (!instance) return;

  editComponentCache[instance.uid] = {
    uiData: uiData,
    instance: instance
  }
}
export function unregisterEditComponent(instance: ComponentInternalInstance) {
  if (!instance) return;
  delete editComponentCache[instance.uid];
}
//#endregion Cache